import { inject, injectable } from 'inversify';
import Cookies from 'js-cookie';

import { IUserService, TYPES as USER_SERVICE_TYPES } from '@desygner/ui-data-access-user';

import { TYPES } from '../../inversify.types';
import { IConfigStoreInjector, IUserStoreInjector, IViewerStoreInjector } from '../../store';
import { IPostMessageManager } from './postmessage-manager';

export interface ICredentialsManager {
  isCredentialValid: () => Promise<boolean>;
  getUserTokenData: () => {
    token: string;
    type: 'Basic' | 'Bearer';
  };
  loadUserToken: () => void;
  init: () => Promise<void>;
  hasSSOLogin: () => boolean;
  ssoLogin: () => void;
  registerSSORevalidateListener: () => void;
  trigger: (event: CredentialEvent) => void;
}

export type CredentialEvent = 'revalidate';

@injectable()
export class CredentialsManager implements ICredentialsManager {
  private events: Partial<Record<CredentialEvent, (() => void)[]>> = {};

  constructor(
    @inject(TYPES.Store.Viewer)
    private readonly viewerStore: IViewerStoreInjector,
    @inject(TYPES.Store.Config)
    private readonly configStore: IConfigStoreInjector,
    @inject(USER_SERVICE_TYPES.UserService)
    private readonly userService: IUserService,
    @inject(TYPES.Store.User)
    private readonly userStore: IUserStoreInjector,
    @inject(TYPES.Viewer.PostMessageManager)
    private readonly postMessageManager: IPostMessageManager
  ) {}

  private getCookieDomain(): string {
    const { cookieDomain } = this.configStore.getState().resources;

    return cookieDomain;
  }

  init = async () => {
    if (this.postMessageManager.isEmbedded()) {
      const credentials = await this.postMessageManager.receiveCredentialsFromParent();

      if (credentials) {
        this.setLegacyCredentials(credentials);
      }
    }

    this.loadUserToken();
  };

  private setLegacyCredentials(credentials: { userToken: string; userHash: string }): void {
    const { userToken, userHash } = credentials;
    const cookieDomain = this.getCookieDomain();

    Cookies.set('user_token', userToken, {
      sameSite: 'none',
      secure: true,
      domain: cookieDomain,
    });

    Cookies.set('user_hash', userHash, {
      sameSite: 'none',
      secure: true,
      domain: cookieDomain,
    });
  }

  public hasSSOLogin() {
    const viewerStoreState = this.viewerStore.getState();
    const { company } = viewerStoreState;
    // const { features } = this.workspaceStore.getState();
    const whiteLabel =
      company && 'white_label_configuration' in company
        ? company.white_label_configuration
        : undefined;
    const isKW =
      whiteLabel?.domain === 'kw' ||
      (whiteLabel?.parent_company && whiteLabel?.parent_company === 'kw');

    if (!viewerStoreState.company) return false;

    return (
      viewerStoreState.company.is_workspace &&
      // features.isFeatureEnabled('sso', 'sso_only') &&
      !isKW
    );
  }

  public async isCredentialValid(): Promise<boolean> {
    try {
      const response = await this.userService.getUserInfo();

      if (!response.data) {
        return false;
      }

      return !!response.data.credentials;
    } catch (error) {
      return false;
    }
  }

  public getUserTokenData(): { token: string; type: 'Basic' | 'Bearer' } {
    const { userToken, auth } = this.userStore.getState();

    return {
      token: userToken,
      type: auth,
    };
  }

  public loadUserToken = () => {
    const { auth, setUserToken } = this.userStore.getState();
    const userToken = Cookies.get('user_token');
    const userHash = Cookies.get('user_hash');

    if (userHash && auth === 'Basic') {
      const token = btoa(`${userToken}:${userHash}`);

      setUserToken(token);

      return token;
    }

    if (userToken) {
      return setUserToken(userToken);
    }
  };

  public ssoLogin() {
    const { company } = this.viewerStore.getState();
    const whiteLabel =
      company && 'white_label_configuration' in company
        ? company.white_label_configuration
        : undefined;

    if (!whiteLabel) return;

    const domain = window.location.host;
    const subdomain = whiteLabel.domain;

    window.open(`https://${subdomain}.${domain}/login`);
    Cookies.set('referer', `https://${domain}/revalidate`, {
      path: '/',
      domain,
    });
  }

  private async ssoRevalidate(event: MessageEvent) {
    const userHash = event.data.credentials.userHash;
    const userToken = event.data.credentials.userToken;
    const { auth, setUserToken } = this.userStore.getState();
    const apiKey = userHash && auth === 'Basic' ? btoa(`${userToken}:${userHash}`) : userToken;

    userHash &&
      Cookies.set('user_hash', userHash, {
        sameSite: 'none',
        secure: true,
        domain: this.getCookieDomain(),
      });

    Cookies.set('user_token', userToken, {
      sameSite: 'none',
      secure: true,
      domain: this.getCookieDomain(),
    });

    Cookies.remove('referer');

    setUserToken(apiKey);
  }

  public registerSSORevalidateListener() {
    const channel = new BroadcastChannel('desygner_editor');

    channel.onmessage = async (event) => {
      if (!event.data.revalidateCredentials) return;

      this.ssoRevalidate(event);
      channel.postMessage({ finished: true });
      channel.close();
    };
  }

  public trigger(event: CredentialEvent) {
    if (!this.events[event]) return;

    this.events[event]?.forEach((callback) => callback());
  }
}
